import { useSyncExternalStore } from "react";
import { isAmbiguousFailure, newClientRequestId } from "./clientRequest";

// The request id (and the reason it was made with) of every action that may already have been
// applied, kept for the whole session and not inside a dialog. A dialog can be cancelled, remounted
// or unmounted by a reload; the person can then open the same row's action again, and it must be
// the same request (same id, same reason) until the service gives a definite answer. A success or a
// definite refusal (an HTTP 4xx other than the idempotency "still running" code) ends it.
//
// `key` names one action on one target, for example `retry_refund:<payment id>` or
// `refund:<subscription id>`.
const entries = new Map();
const listeners = new Set();
let version = 0;

const changed = () => {
  version += 1;
  listeners.forEach((listener) => listener());
};

// The id to send. The same fingerprint (the whole body) keeps the id the last try used; a different
// body is a different action and gets a new one.
export const pendingRequestId = (key, fingerprint) => {
  const held = entries.get(key);
  if (held && held.fingerprint === fingerprint) {
    return held.id;
  }
  entries.set(key, { fingerprint, id: newClientRequestId(), ambiguous: false, reason: null });
  return entries.get(key).id;
};

// Call with every answer. `reason` is what the person chose or typed, kept so a reopened dialog
// shows it again, locked. Returns true when the answer left the action unresolved.
export const recordAnswer = (key, result, reason) => {
  const held = entries.get(key);
  if (!result.ok && isAmbiguousFailure(result)) {
    if (held) {
      held.ambiguous = true;
      held.reason = reason;
    }
    changed();
    return true;
  }
  if (held) {
    entries.delete(key);
    changed();
  }
  return false;
};

// The unresolved entry of an action ({ id, reason, ... }), or null.
export const unresolvedFor = (key) => {
  const held = entries.get(key);
  return held && held.ambiguous ? held : null;
};

// The person (or a fresh read of the data) settled it another way, for example the subscription now
// reads as refunded.
export const clearPending = (key) => {
  if (entries.delete(key)) {
    changed();
  }
};

// Re-renders the caller whenever any entry changes; read `unresolvedFor` during render.
export const usePendingRequests = () =>
  useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => version
  );
